// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useLazyQuery, useMutation } from '@apollo/client';
import type { SelectChangeEvent } from '@mui/material/Select';
import type { DateValidationError } from '@mui/x-date-pickers';
import { clientLogger } from '@seedplanner/logger';
import {
  MAX_ORDER_WEEKLY_FREQUENCY,
  ORDER_STATUS,
  dayOfWeekToNumber,
  formatTime,
  getRecurringOrderTimesToCreate,
  numberToDayOfWeek,
  utils,
} from '@seedplanner/shared-utils';
import dayjs from 'dayjs';
import timezonePlugin from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import { useUserContext } from '../../context/UserContext';
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '../../lib/base';
import { Help } from '../../lib/help';
import { AddCircleOutlineIcon } from '../../lib/icons';
import { CUSTOMERS_QUERY } from '../../queries/CustomersQuery';
import { DASHBOARD_OVERDUE_TASKS_QUERY, DASHBOARD_QUERY } from '../../queries/DashboardQueries';
import { CROPS_BLENDS_QUERY } from '../../queries/OrderFormCropsQuery';
import { RECURRING_ORDERS_QUERY } from '../../queries/RecurringOrderQueries';
import type {
  CreateOrderMutation,
  CreateOrderMutationVariables,
  CreateRecurringOrderMutation,
  CreateRecurringOrderMutationVariables,
  NewOrderSurplusesDataQuery,
  NewOrderSurplusesDataQueryVariables,
  OrderFormCropsQuery,
  OrderFormCropsQueryVariables,
  OrderFormOrderDataQuery,
  OrderFormOrderDataQueryVariables,
  OrderFormRecurringOrderDataQuery,
  OrderFormRecurringOrderDataQueryVariables,
} from '../../types/__generated__/graphql';
import { createID, createOnBlurRequired, getID, getTimeRanges } from '../../utils';
import { ExitDialogButton } from '../common/button';
import InternalDatePicker from '../common/date/InternalDatePicker';
import { ErrorView } from '../common/error';
import {
  DEFAULT_FREQUENCY_UNIT,
  EVERY_NTH_WEEKDAY_OF_MONTH,
  EVERY_X_DAY_OF_WEEK,
  type FrequencyLabel,
  daysOfTheWeek,
  frequencyLabelToInput,
  orderFrequencyOptions,
  setFrequencyStates,
} from '../common/frequency';
import { PageLoading } from '../common/loading';
import { getLongestGrowTimePlusBufferFromInputForms } from '../recurringOrder/orderUtils';
import type { AddOrderChangeInfo } from './AddOrderChangesTable';
import AddOrderChangesTable from './AddOrderChangesTable';
import NewCropOrBlendToUseForm, {
  type CropOrBlendToUseFormInput,
  type CropData,
  type BlendData,
  type Props as NewCropOrBlendToUseFormProps,
} from './NewCropOrBlendToUseForm';
import { checkGrowTimeAndDeliveryDate } from './checkGrowTimeAndDeliveryDate';
import { getOrderChanges } from './getOrderChanges';
import { getTraysFromCropsAndBlends } from './getTrayCounts';
import {
  filterParseSortBlendData,
  filterParseSortCropData,
  inputsToNewCropOrBlendToUseFormProps,
  prepareFrequencyForSubmit,
  preprareFormsForSubmit,
} from './orderFormUtils';
import { createSizeKey } from './sizeUtils';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

const CREATE_ORDER_MUTATION = gql`
  mutation CreateOrder(
    $order_fulfillment_time: Int!
    $order_status: String
    $customer: String!
    $notes: String
    $use_crops: [CreateOrderUseCropsInput!]!
    $use_blends: [CreateOrderUseBlendsInput!]!
    $use_surpluses: Boolean!
    $create_overdue_plantings: Boolean!
  ) {
    createOrder(
      input: {
        order_fulfillment_time: $order_fulfillment_time
        order_status: $order_status
        customer: $customer
        notes: $notes
        use_crops: $use_crops
        use_blends: $use_blends
        use_surpluses: $use_surpluses
        create_overdue_plantings: $create_overdue_plantings
      }
    ) {
      order_created {
        id
      }
      plantings_created {
        id
      }
      tasks_created {
        id
      }
      plantings_updated {
        id
        trays_rounded
        planting_state
        order_id
        updated_at
      }
      tasks_updated {
        id
        order_id
        updated_at
      }
    }
  }
`;
const CREATE_RECURRING_ORDER_MUTATION = gql`
  mutation CreateRecurringOrder(
    $customer: String!
    $notes: String
    $use_crops: [CreateOrderUseCropsInput!]!
    $use_blends: [CreateOrderUseBlendsInput!]!
    $recurring_start_date: Int!
    $recurring_end_date: Int
    $frequency_options: RecurringOrderOptionsInput!
    $should_create_overdue_orders: Boolean
  ) {
    createRecurringOrder(
      input: {
        customer: $customer
        notes: $notes
        use_crops: $use_crops
        use_blends: $use_blends
        recurring_start_date: $recurring_start_date
        recurring_end_date: $recurring_end_date
        frequency_options: $frequency_options
        should_create_overdue_orders: $should_create_overdue_orders
      }
    ) {
      recurring_order_created {
        id
      }
      orders_created {
        id
      }
      plantings_created {
        id
      }
      tasks_created {
        id
      }
    }
  }
`;
const ORDER_QUERY = gql`
  query OrderFormOrderData($id: ID!) {
    order(id: $id) {
      id
      order_status
      customer
      notes
      order_fulfillment_time
      inputs {
        edges {
          node {
            id
            size_amount
            quantity
            unit
            price_per_item
            product_id
            crop {
              id
              crop_name
              status
              total_grow_time
              harvest_buffer_time
              yield_per_tray
              yield_unit
              buffer_percentage
              edge_sizes {
                edges {
                  node {
                    id
                    size
                    unit
                    price_per_item
                  }
                }
              }
            }
            blend {
              id
              blend_name
              status
              edge_sizes {
                edges {
                  node {
                    id
                    size
                    unit
                    price_per_item
                  }
                }
              }
              crops {
                edges {
                  node {
                    id
                    percent_of_blend
                    crop {
                      id
                      total_grow_time
                      harvest_buffer_time
                      yield_per_tray
                      yield_unit
                      crop_name
                      buffer_percentage
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    crops(
      filters: [{ name: "crop.status", operator: "!=", value: "archived" }]
      orderBy: [{ name: "crop.crop_name", direction: "asc" }]
    ) {
      edges {
        node {
          id
          crop_name
          status
          total_grow_time
          harvest_buffer_time
          yield_per_tray
          yield_unit
          buffer_percentage
          edge_sizes {
            edges {
              node {
                id
                size
                unit
                price_per_item
              }
            }
          }
        }
      }
    }
    blends(
      filters: [{ name: "blend.status", operator: "!=", value: "archived" }]
      orderBy: [{ name: "blend.blend_name", direction: "asc" }]
    ) {
      edges {
        node {
          id
          blend_name
          status
          edge_sizes {
            edges {
              node {
                id
                size
                unit
                price_per_item
              }
            }
          }
          crops {
            edges {
              node {
                id
                percent_of_blend
                crop {
                  id
                  crop_name
                  total_grow_time
                  harvest_buffer_time
                  yield_per_tray
                  yield_unit
                  buffer_percentage
                }
              }
            }
          }
        }
      }
    }
  }
`;
const SURPLUSES_QUERY = gql`
  query NewOrderSurplusesData(
    $todayStart: Int!
    $orderTime: Int!
  ) {
    surpluses(
      timeAfter: $todayStart
      timeBefore: $orderTime
    ) {
      edges {
        node {
          plantings {
            id
            trays_rounded
            planting_time
            crop {
              id
              max_hold_time
              harvest_buffer_time
            }
          }
        }
      }
    }
  }
`;
const RECURRING_ORDER_QUERY = gql`
  query OrderFormRecurringOrderData($id: ID!) {
    recurring_order(id: $id) {
      id
      customer
      notes
      recurring_start_time
      recurring_end_time
      recurring_options {
        frequencyGroups {
          frequencyType
          frequencies
        }
      }
      inputs {
        edges {
          node {
            id
            size_amount
            quantity
            unit
            price_per_item
            product_id
            crop {
              id
              crop_name
              status
              total_grow_time
              harvest_buffer_time
              yield_per_tray
              yield_unit
              buffer_percentage
              edge_sizes {
                edges {
                  node {
                    id
                    size
                    unit
                    price_per_item
                  }
                }
              }
            }
            blend {
              id
              blend_name
              status
              edge_sizes {
                edges {
                  node {
                    id
                    size
                    unit
                    price_per_item
                  }
                }
              }
              crops {
                edges {
                  node {
                    id
                    percent_of_blend
                    crop {
                      id
                      total_grow_time
                      harvest_buffer_time
                      yield_per_tray
                      yield_unit
                      crop_name
                      buffer_percentage
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    crops(
      filters: [{ name: "crop.status", operator: "!=", value: "archived" }]
      orderBy: [{ name: "crop.crop_name", direction: "asc" }]
    ) {
      edges {
        node {
          id
          crop_name
          status
          total_grow_time
          harvest_buffer_time
          yield_per_tray
          yield_unit
          buffer_percentage
          edge_sizes {
            edges {
              node {
                id
                size
                unit
                price_per_item
              }
            }
          }
        }
      }
    }
    blends(
      filters: [{ name: "blend.status", operator: "!=", value: "archived" }]
      orderBy: [{ name: "blend.blend_name", direction: "asc" }]
    ) {
      edges {
        node {
          id
          blend_name
          status
          edge_sizes {
            edges {
              node {
                id
                size
                unit
                price_per_item
              }
            }
          }
          crops {
            edges {
              node {
                id
                percent_of_blend
                crop {
                  id
                  crop_name
                  total_grow_time
                  harvest_buffer_time
                  yield_per_tray
                  yield_unit
                  buffer_percentage
                }
              }
            }
          }
        }
      }
    }
  }
`;

interface Props {
  readonly cloneOrderInfo?: {
    readonly orderId: number;
    readonly orderTime: number;
  };
  readonly recurringOrderId?: number;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
  readonly isForNewSingleOrder: boolean;
}

export default function AddOrderDialog({
  cloneOrderInfo,
  recurringOrderId,
  isOpen,
  onClose,
  onSuccess,
  isForNewSingleOrder,
}: Props) {
  const { timezone, preferredWeightUnit } = useUserContext();
  const { todayStart, todayStartDate, sevenDaysFromTodayStart } = getTimeRanges(timezone);
  const [createOrder, { loading: createOrderLoading }] = useMutation<CreateOrderMutation, CreateOrderMutationVariables>(
    CREATE_ORDER_MUTATION,
  );
  const [createRecurringOrder, { loading: createRecurringOrderLoading }] = useMutation<
    CreateRecurringOrderMutation,
    CreateRecurringOrderMutationVariables
  >(CREATE_RECURRING_ORDER_MUTATION);
  const [getCropData] = useLazyQuery<OrderFormCropsQuery, OrderFormCropsQueryVariables>(CROPS_BLENDS_QUERY, {
    defaultOptions: {
      fetchPolicy: 'network-only',
    },
  });
  const [getOrderData, { data: orderData, loading: orderDataLoading, error: orderDataError }] = useLazyQuery<
    OrderFormOrderDataQuery,
    OrderFormOrderDataQueryVariables
  >(ORDER_QUERY, {
    defaultOptions: {
      fetchPolicy: 'network-only',
    },
  });
  const [
    getRecurringOrderData,
    { data: recurringOrderData, loading: recurringOrderDataLoading, error: recurringOrderDataError },
  ] = useLazyQuery<OrderFormRecurringOrderDataQuery, OrderFormRecurringOrderDataQueryVariables>(RECURRING_ORDER_QUERY, {
    defaultOptions: {
      fetchPolicy: 'network-only',
    },
  });
  const [getSurplusesData, { data: surplusesData }] = useLazyQuery<
    NewOrderSurplusesDataQuery,
    NewOrderSurplusesDataQueryVariables
  >(SURPLUSES_QUERY, {
    defaultOptions: {
      fetchPolicy: 'network-only',
    },
  });

  const [filteredCropsData, setFilteredCropsData] = React.useState<CropData[]>([]);
  const [blendsData, setBlendsData] = React.useState<BlendData[]>([]);

  const isForSingleOrderClone = cloneOrderInfo !== undefined;
  const isForRecurringOrderClone = recurringOrderId !== undefined;
  const isForNewOrder = !isForSingleOrderClone && !isForRecurringOrderClone && isForNewSingleOrder;
  const isForNewRecurringOrder = !isForSingleOrderClone && !isForRecurringOrderClone && !isForNewSingleOrder;

  React.useEffect(() => {
    if (isOpen) {
      if (isForNewOrder || isForNewRecurringOrder) {
        void getCropData({
          onCompleted: (cropDataIn) => {
            if (cropDataIn?.crops != null) {
              const readyFilteredCropsData = filterParseSortCropData(cropDataIn.crops);
              setFilteredCropsData(readyFilteredCropsData);
            }
            if (cropDataIn?.blends != null) {
              const readyBlendsData = filterParseSortBlendData(cropDataIn.blends);
              setBlendsData(readyBlendsData);
            }
          },
          onError: (errorIn) => {
            enqueueSnackbar(`Problem getting crop data: ${errorIn.message}`, {
              variant: 'error',
              autoHideDuration: 10000,
            });
          },
        });
      } else if (isForSingleOrderClone) {
        void getOrderData({
          variables: { id: createID.order(cloneOrderInfo.orderId) },
          onCompleted: (orderDataIn) => {
            if (orderDataIn?.order != null && orderDataIn?.crops != null) {
              const readyFilteredCropsData = filterParseSortCropData(orderDataIn.crops);
              setFilteredCropsData(readyFilteredCropsData);

              const readyBlendsData = filterParseSortBlendData(orderDataIn.blends);
              setBlendsData(readyBlendsData);

              setCustomerName(orderDataIn.order.customer ?? '');
              setOrderStatus(ORDER_STATUS.PLANNED);
              setNotes(orderDataIn.order.notes ?? '');
              setIsRecurring(false);
              if (orderDataIn.order.order_fulfillment_time != null) {
                setFulfillmentTime(dayjs.unix(orderDataIn.order.order_fulfillment_time).tz(timezone));
              }
              const inputs = orderDataIn.order.inputs.edges.map(({ node }) => node);
              const { cropFormValues, blendFormValues } = inputsToNewCropOrBlendToUseFormProps({
                orderInputs: inputs,
                preferredWeightUnit,
              });
              setCropOrBlendForms(
                cropFormValues.concat(blendFormValues).map((values, idx) => {
                  const formKey = utils.getRandomInt();
                  return (
                    <NewCropOrBlendToUseForm
                      key={formKey}
                      formKey={formKey}
                      index={idx}
                      disableFields={disableFields}
                      values={values}
                      onChangeField={handleCropOrBlendFormInputChange}
                      onRemove={removeCropOrBlendForm}
                      cropsData={readyFilteredCropsData}
                      blendsData={readyBlendsData}
                    />
                  );
                }),
              );
            }
          },
          onError: (errorIn) => {
            enqueueSnackbar(`Problem getting order data: ${errorIn.message}`, {
              variant: 'error',
              autoHideDuration: 10000,
            });
          },
        });
        void getSurplusesData({
          variables: {
            todayStart: todayStart,
            orderTime: utils.toNoonTime(dayjs.unix(cloneOrderInfo.orderTime).tz(timezone), timezone).unix(),
          },
          onError: () => {
            enqueueSnackbar('Something went wrong fetching surpluses!', { variant: 'error', autoHideDuration: 10000 });
          },
        });
      } else if (isForRecurringOrderClone) {
        void getRecurringOrderData({
          variables: { id: createID.recurringOrder(recurringOrderId) },
          onCompleted: (orderDataIn) => {
            if (orderDataIn?.recurring_order != null && orderDataIn?.crops != null) {
              const readyFilteredCropsData = filterParseSortCropData(orderDataIn.crops);
              setFilteredCropsData(readyFilteredCropsData);

              const readyBlendsData = filterParseSortBlendData(orderDataIn.blends);
              setBlendsData(readyBlendsData);

              setCustomerName(orderDataIn.recurring_order.customer ?? '');
              setNotes(orderDataIn.recurring_order.notes ?? '');
              setIsRecurring(true);

              setFrequencyStates({
                timezone,
                recurringOptions: orderDataIn.recurring_order.recurring_options,
                startTime: Math.max(
                  orderDataIn.recurring_order.recurring_start_time,
                  utils.todayStart(timezone).unix(),
                ),
                endTime: orderDataIn.recurring_order.recurring_end_time,
                setFrequencyUnit,
                setFrequency,
                setDayOfWeek,
                setOccurence,
                setRecursForever,
                setStartDate,
                setEndDate,
              });

              const inputs = orderDataIn.recurring_order.inputs.edges.map(({ node }) => node);
              const { cropFormValues, blendFormValues } = inputsToNewCropOrBlendToUseFormProps({
                orderInputs: inputs,
                preferredWeightUnit,
              });
              setCropOrBlendForms(
                cropFormValues.concat(blendFormValues).map((values, idx) => {
                  const formKey = utils.getRandomInt();
                  return (
                    <NewCropOrBlendToUseForm
                      key={formKey}
                      formKey={formKey}
                      index={idx}
                      disableFields={disableFields}
                      values={values}
                      onChangeField={handleCropOrBlendFormInputChange}
                      onRemove={removeCropOrBlendForm}
                      cropsData={readyFilteredCropsData}
                      blendsData={readyBlendsData}
                    />
                  );
                }),
              );
            }
          },
        });
      }
    }
  }, [
    isOpen,
    getCropData,
    getOrderData,
    getRecurringOrderData,
    timezone,
    isForSingleOrderClone,
    isForRecurringOrderClone,
    isForNewOrder,
    isForNewRecurringOrder,
    cloneOrderInfo,
    recurringOrderId,
    getSurplusesData,
    todayStart,
    preferredWeightUnit,
  ]);

  const disableFields = createOrderLoading || createRecurringOrderLoading;

  const [customerName, setCustomerName] = React.useState('');
  const [orderStatus, setOrderStatus] = React.useState(ORDER_STATUS.PLANNED);
  const [notes, setNotes] = React.useState('');
  const [fulfillmentTime, setFulfillmentTime] = React.useState<dayjs.Dayjs | null>(null);
  const [startDate, setStartDate] = React.useState<dayjs.Dayjs | null>(utils.todayStart());
  const [endDate, setEndDate] = React.useState<dayjs.Dayjs | null>(null);
  const [cropOrBlendForms, setCropOrBlendForms] = React.useState<React.ReactElement<NewCropOrBlendToUseFormProps>[]>(
    [],
  );
  const [isRecurring, setIsRecurring] = React.useState(!isForNewSingleOrder);
  const [recursForever, setRecursForever] = React.useState(!isForNewSingleOrder);
  // Frequency for every X weeks
  const [frequency, setFrequency] = React.useState('1');
  // Frequency type: every X weeks, every X day of the week, every nth weekday of the month
  const [frequencyUnit, setFrequencyUnit] = React.useState(DEFAULT_FREQUENCY_UNIT);
  // Day of the week for every nth weekday of the month or for every X day of the week
  const [dayOfWeek, setDayOfWeek] = React.useState(numberToDayOfWeek(dayjs().day()));
  // Occurence for every nth weekday of the month
  const [occurence, setOccurence] = React.useState('1');
  const [shouldCreateOverdueOrders, setShouldCreateOverdueOrders] = React.useState(true);
  const [useSurpluses, setUseSurpluses] = React.useState(true);
  const [createOverduePlantings, setCreateOverduePlantings] = React.useState(true);

  const [startDateError, setStartDateError] = React.useState<DateValidationError | null>(null);

  const startDateErrorMessage = React.useMemo(() => {
    if (startDateError == null) {
      return '';
    }
    switch (startDateError) {
      case 'maxDate':
        return 'Start date cannot be after end date';
      case 'minDate':
        return 'Start date cannot be before today';
      default:
        return '';
    }
  }, [startDateError]);

  const onFrequencyUnitChange = (e: SelectChangeEvent<string>) => {
    setFrequencyUnit(e.target.value);
  };
  const onDayOfWeekChange = (e: SelectChangeEvent<string>) => {
    setDayOfWeek(e.target.value);
  };
  const onChangeFrequencySelect = (e: SelectChangeEvent<string>) => {
    setFrequency(e.target.value);
  };
  const onChangeOccurence = (e: SelectChangeEvent<string>) => {
    setOccurence(e.target.value);
  };
  const onChangeEndDate = (newValue: dayjs.Dayjs | null) => {
    setEndDate(newValue);
  };
  const onChangeIsRecurring = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsRecurring(e.target.checked);
  };
  const onChangeRecursForever = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRecursForever(e.target.checked);
  };
  // const onChangeOrderStatus = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
  //   setOrderStatus(e.target.value);
  // };
  const onChangeNotes = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setNotes(e.target.value);
  };
  const onChangeCustomerName = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.target.value !== '') {
      setCustomerNameValidation('');
    }
    setCustomerName(e.target.value);
  };
  const onChangeFullfillmentTime = (newValue: dayjs.Dayjs | null) => {
    // Refetch surpluses with new order time
    // Only fetch if time changed and is not null
    if (newValue != null && dayjs(newValue).isValid() && !dayjs(newValue).isSame(fulfillmentTime, 'day')) {
      const maybeNewOrderTime = utils.toNoonTime(newValue, timezone).unix();
      if (maybeNewOrderTime != null && !Number.isNaN(maybeNewOrderTime) && maybeNewOrderTime > 0) {
        void getSurplusesData({
          variables: {
            todayStart,
            orderTime: utils.toNoonTime(newValue, timezone).unix(),
          },
          onError: () => {
            enqueueSnackbar('Something went wrong fetching surpluses!', { variant: 'error', autoHideDuration: 10000 });
          },
        });
      }
    }
    setFulfillmentTime(newValue);
  };
  const onChangeStartDate = (newValue: dayjs.Dayjs | null) => {
    setStartDate(newValue);
  };
  const onChangeShouldCreateOverdueOrders = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShouldCreateOverdueOrders(e.target.checked);
  };
  const onChangeUseSurpluses = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUseSurpluses(e.target.checked);
  };
  const onChangeCreateOverduePlantings = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCreateOverduePlantings(e.target.checked);
  };
  const [customerNameValidation, setCustomerNameValidation] = React.useState('');
  const onCustomerNameBlur = createOnBlurRequired('Customer name is required', setCustomerNameValidation);

  const addCropOrBlendToUseForm = () => {
    setCropOrBlendForms((prevForms) => {
      const formKey = utils.getRandomInt();
      return [
        ...prevForms,
        <NewCropOrBlendToUseForm
          key={formKey}
          formKey={formKey}
          index={prevForms.length}
          disableFields={disableFields}
          values={{
            cropOrBlendSelected: undefined,
            itemCount: '',
            harvestTime: dayjs(),
            itemCountValidation: '',
            countUnit: createSizeKey({
              size: null,
              unit: preferredWeightUnit,
              pricePerItem: null,
              productId: null,
            }),
            pricePerItem: '',
            pricePerItemValidation: '',
          }}
          onChangeField={handleCropOrBlendFormInputChange}
          onRemove={removeCropOrBlendForm}
          cropsData={filteredCropsData}
          blendsData={blendsData}
        />,
      ];
    });
  };
  const removeCropOrBlendForm = (index: number) => {
    setCropOrBlendForms((prevForms) => {
      const updatedForms = [...prevForms];
      updatedForms.splice(index, 1);
      return updatedForms.map((old, idx) => React.cloneElement(old, { index: idx }));
    });
  };
  const handleCropOrBlendFormInputChange = (
    index: number,
    fieldName: keyof CropOrBlendToUseFormInput,
    value: string | number | null | undefined | CropData | BlendData,
    validationFieldName?: string,
  ) => {
    setCropOrBlendForms((prevForms) => {
      const updatedForms = [...prevForms];
      // If fieldName is 'cropOrBlendSelected', then check if the countUnit exists in the possible options
      // for the newly selected crop. If not then reset the countUnit to the default
      const existingFormValues: CropOrBlendToUseFormInput = updatedForms[index].props.values;
      let newCountUnit = existingFormValues.countUnit;
      if (fieldName === 'cropOrBlendSelected') {
        const newValue = value as CropData | BlendData;
        const existingCountUnit = existingFormValues.countUnit;
        const possibleSizeKeys = newValue.sizes.map((size) =>
          createSizeKey({
            size: size.size,
            unit: size.unit,
            pricePerItem: size.price_per_item ?? null,
            productId: size.product_id ?? null,
          }),
        );

        if (!possibleSizeKeys.includes(existingCountUnit)) {
          newCountUnit = createSizeKey({
            size: null,
            unit: preferredWeightUnit,
            pricePerItem: null,
            productId: null,
          });
        }
      }
      const newValues: CropOrBlendToUseFormInput = {
        ...updatedForms[index].props.values,
        countUnit: newCountUnit,
        // This must come after countUnit is set
        // In case the countUnit is the value being changed
        [fieldName]: value,
      };
      if (validationFieldName !== undefined && value !== '') {
        // @ts-ignore
        newValues[validationFieldName] = '';
      }
      updatedForms[index] = React.cloneElement(updatedForms[index], {
        values: newValues,
      });

      return updatedForms;
    });
  };

  const onSubmit = () => {
    const { cropsToUse, blendsToUse } = preprareFormsForSubmit(cropOrBlendForms);
    const recurringOrderInput = prepareFrequencyForSubmit({
      startDate,
      endDate,
      frequencyUnit,
      frequency,
      dayOfWeek,
      occurence,
      recursForever,
      timezone,
    });
    if (isRecurring) {
      void createRecurringOrder({
        refetchQueries: [
          {
            query: DASHBOARD_QUERY,
            variables: {
              todayStart: todayStart.toString(),
              sevenDaysFromTodayStart: sevenDaysFromTodayStart.toString(),
            },
          },
          {
            query: DASHBOARD_OVERDUE_TASKS_QUERY,
            variables: {
              todayStart: todayStart.toString(),
              first: 25,
            },
          },
          { query: CUSTOMERS_QUERY },
          { query: RECURRING_ORDERS_QUERY, variables: { todayStart: todayStart.toString() } },
        ],
        variables: {
          customer: customerName,
          notes: notes === '' ? null : notes,
          use_crops: cropsToUse,
          use_blends: blendsToUse,
          frequency_options: recurringOrderInput.options,
          recurring_start_date: recurringOrderInput.start_date,
          recurring_end_date: recurringOrderInput.end_date,
          should_create_overdue_orders: shouldCreateOverdueOrders,
        },
        update: (cache, { data }) => {
          const plantingsCreated = (data?.createRecurringOrder?.plantings_created?.length ?? 0) > 0;
          const tasksCreated = (data?.createRecurringOrder?.tasks_created?.length ?? 0) > 0;
          const ordersCreated = (data?.createRecurringOrder?.orders_created?.length ?? 0) > 0;
          cache.modify({
            fields: {
              harvests(existingGroup, { DELETE }) {
                return plantingsCreated ? DELETE : existingGroup;
              },
              plantings(existingGroup, { DELETE }) {
                return plantingsCreated ? DELETE : existingGroup;
              },
              taskGroup(existingGroup, { DELETE }) {
                return tasksCreated ? DELETE : existingGroup;
              },
              plantingGroup(existingGroup, { DELETE }) {
                return plantingsCreated ? DELETE : existingGroup;
              },
              plantingTimeGroup(existingGroup, { DELETE }) {
                return plantingsCreated ? DELETE : existingGroup;
              },
              orders(existingOrders, { DELETE }) {
                return ordersCreated ? DELETE : existingOrders;
              },
            },
          });
        },
        onCompleted: (dataIn) => {
          if (dataIn !== undefined && dataIn.createRecurringOrder != null) {
            const newOrderData = dataIn.createRecurringOrder;
            enqueueSnackbar(`Created Recurring Order ${getID(newOrderData.recurring_order_created.id)}`, {
              variant: 'success',
            });
            setCustomerName('');
            setOrderStatus(ORDER_STATUS.PLANNED);
            setNotes('');
            setFulfillmentTime(null);
            setCropOrBlendForms([]);
            setStartDate(utils.todayStart());
            setEndDate(null);
            setFrequencyUnit(DEFAULT_FREQUENCY_UNIT);
            setIsRecurring(true);
            setRecursForever(true);
            setFrequency('');
            setOccurence('1');
            setDayOfWeek('');
            setShouldCreateOverdueOrders(false);
            onSuccess();
          } else {
            enqueueSnackbar('Error creating recurring order', { variant: 'error', autoHideDuration: 10000 });
          }
        },
        onError: (errorIn) => {
          enqueueSnackbar(errorIn.message, {
            variant: 'error',
            autoHideDuration: 10000,
          });
          clientLogger.error({ title: 'new_order_error', message: errorIn.message });
        },
      });
    } else {
      void createOrder({
        refetchQueries: [
          {
            query: DASHBOARD_QUERY,
            variables: {
              todayStart: todayStart.toString(),
              sevenDaysFromTodayStart: sevenDaysFromTodayStart.toString(),
            },
          },
          {
            query: DASHBOARD_OVERDUE_TASKS_QUERY,
            variables: {
              todayStart: todayStart.toString(),
              first: 25,
            },
          },
          { query: CUSTOMERS_QUERY },
        ],
        update: (cache, { data }) => {
          const plantingsCreated = (data?.createOrder?.plantings_created?.length ?? 0) > 0;
          const tasksCreated = (data?.createOrder?.tasks_created?.length ?? 0) > 0;
          cache.modify({
            fields: {
              harvests(existingGroup, { DELETE }) {
                return plantingsCreated ? DELETE : existingGroup;
              },
              plantings(existingGroup, { DELETE }) {
                return plantingsCreated ? DELETE : existingGroup;
              },
              task_groups(existingGroup, { DELETE }) {
                return tasksCreated ? DELETE : existingGroup;
              },
              tasks(existingTasks, { DELETE }) {
                return tasksCreated ? DELETE : existingTasks;
              },
              taskGroup(existingGroup, { DELETE }) {
                return tasksCreated ? DELETE : existingGroup;
              },
              plantingGroup(existingGroup, { DELETE }) {
                return plantingsCreated ? DELETE : existingGroup;
              },
              planting_groups(existingGroup, { DELETE }) {
                return plantingsCreated ? DELETE : existingGroup;
              },
              plantingTimeGroup(existingGroup, { DELETE }) {
                return plantingsCreated ? DELETE : existingGroup;
              },
              orders(_, { DELETE }) {
                return DELETE;
              },
            },
          });
        },
        variables: {
          order_fulfillment_time: fulfillmentTime == null ? 0 : utils.toNoonTime(fulfillmentTime, timezone).unix(),
          customer: customerName,
          order_status: orderStatus,
          notes: notes === '' ? null : notes,
          use_crops: cropsToUse,
          use_blends: blendsToUse,
          use_surpluses: useSurpluses,
          create_overdue_plantings: createOverduePlantings,
        },
        onCompleted: (dataIn) => {
          if (dataIn !== undefined && dataIn.createOrder != null) {
            const newOrderData = dataIn.createOrder;
            enqueueSnackbar(`Created Order ${getID(newOrderData.order_created.id)}`, { variant: 'success' });
            setCustomerName('');
            setOrderStatus(ORDER_STATUS.PLANNED);
            setNotes('');
            setFulfillmentTime(null);
            setCropOrBlendForms([]);
            setStartDate(utils.todayStart());
            setEndDate(null);
            setFrequencyUnit(DEFAULT_FREQUENCY_UNIT);
            setIsRecurring(true);
            setRecursForever(true);
            setFrequency('');
            setOccurence('1');
            setDayOfWeek('');
            setShouldCreateOverdueOrders(false);
            onSuccess();
          } else {
            enqueueSnackbar('Error creating order', { variant: 'error', autoHideDuration: 10000 });
          }
        },
        onError: (errorIn) => {
          enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
          clientLogger.error({ title: 'new_order_error', message: errorIn.message });
        },
      });
    }
  };

  const hasInputValidationError =
    startDateErrorMessage !== '' ||
    cropOrBlendForms.some(
      ({ props }) => props.values.itemCountValidation !== '' && props.values.pricePerItemValidation !== '',
    );

  const requiredFieldsFilled: boolean =
    customerName !== '' &&
    (isRecurring ? startDate !== null : fulfillmentTime !== null) &&
    cropOrBlendForms.length > 0 &&
    cropOrBlendForms.every(
      ({
        props: {
          values: { cropOrBlendSelected, itemCount },
        },
      }) => cropOrBlendSelected !== undefined && itemCount !== '' && Number.parseInt(itemCount) !== 0,
    ) &&
    (isRecurring
      ? (frequencyUnit === EVERY_X_DAY_OF_WEEK ? dayOfWeek !== '' : frequency !== '') &&
        (recursForever ? true : endDate !== null)
      : true);
  const isSubmitDisabled: boolean = !requiredFieldsFilled || hasInputValidationError || disableFields;
  const onEnter = () => {
    if (!isSubmitDisabled) {
      onSubmit();
    }
  };

  const traysFromForms = getTraysFromCropsAndBlends(cropOrBlendForms);
  const changes = getOrderChanges({
    traysFromForms,
    orderPlantingData: [],
    surplusPlantings: surplusesData?.surpluses.edges.flatMap(({ node }) => node.plantings) ?? [],
    orderFulfillmentTime: fulfillmentTime ? utils.toNoonTime(fulfillmentTime, timezone) : null,
    todayStartDate,
  });

  // For each change get start date and if it's too late to plant and surplus trays available
  const allChanges: AddOrderChangeInfo[] = !isRecurring
    ? changes.map((change) => {
        const plantingDateInfo = fulfillmentTime
          ? checkGrowTimeAndDeliveryDate({
              cropTotalGrowTime: change.totalGrowTime,
              deliveryDate: fulfillmentTime,
              userTimezone: timezone,
              bufferTime: change.harvestBufferTime,
            })
          : undefined;

        return {
          cropId: change.cropId,
          cropName: change.cropName,
          traysNeeded: change.newTrays,
          surplusTraysAvailable: change.surplusTraysAvailable,
          plantingDateInfo,
        };
      })
    : [];

  const longestTotalGrowTimePlusBuffer = getLongestGrowTimePlusBufferFromInputForms(cropOrBlendForms);
  const newOrderTimes = getRecurringOrderTimesToCreate({
    recurringStartTime: startDate?.unix() || utils.todayStart(timezone).unix(),
    recurringEndTime: recursForever ? undefined : endDate?.unix() || undefined, // endDate?.unix() can be NaN
    timezone,
    recurringOptions: {
      exceptions: [],
      frequencyGroups: [
        {
          frequencyType: frequencyLabelToInput(frequencyUnit as FrequencyLabel),
          frequencies:
            frequencyUnit === EVERY_NTH_WEEKDAY_OF_MONTH
              ? [dayOfWeekToNumber(dayOfWeek || 'mon'), Number.parseInt(occurence || '1')]
              : [
                  frequencyUnit === EVERY_X_DAY_OF_WEEK
                    ? dayOfWeekToNumber(dayOfWeek || 'mon')
                    : Number.parseInt(frequency || '1'),
                ],
        },
      ],
    },
    longestTotalGrowTimePlusBuffer,
    ignoreGrowTimeLimit: true,
  }).map((time) => ({
    time,
    isOverdue: time - longestTotalGrowTimePlusBuffer < todayStart,
    formattedTime: formatTime(time, { timezone }),
  }));

  const overdueTimes = newOrderTimes.filter((time) => time.isOverdue);
  const futureTimes = newOrderTimes.filter((time) => !time.isOverdue);

  const form = (
    <Box display="flex" flexDirection="column" sx={{ m: 1 }} gap={2}>
      <TextField
        aria-describedby="customer-name"
        id="customer-name"
        label="Customer Name"
        value={customerName}
        onChange={onChangeCustomerName}
        error={customerNameValidation !== ''}
        subtext={customerNameValidation}
        onBlur={onCustomerNameBlur}
        onEnter={onEnter}
        hasSubtext
        required
        disabled={disableFields}
      />
      <Box display="flex" flexDirection="row" flexWrap="nowrap" gap={2}>
        <Box display="flex" flexDirection="column" sx={{ pl: '14px' }}>
          <FormControl required>
            <FormLabel id="order-is-recurring-label">Recurring Order</FormLabel>
            <Switch checked={isRecurring} onChange={onChangeIsRecurring} />
          </FormControl>
        </Box>
        {isRecurring && (
          <Box display="flex" flexDirection="column" sx={{ pl: '14px' }}>
            <FormControl required>
              <FormLabel id="order-recurs-forever-label">Recur Forever</FormLabel>
              <Switch checked={recursForever} onChange={onChangeRecursForever} />
            </FormControl>
          </Box>
        )}
      </Box>
      <Box display="flex" flexDirection="row" flexWrap="nowrap" alignItems="center" gap={1}>
        <InternalDatePicker
          sx={{ flexGrow: 1 }}
          label={isRecurring ? 'Start Date *' : 'Order Date *'}
          value={isRecurring ? startDate : fulfillmentTime}
          onChange={isRecurring ? onChangeStartDate : onChangeFullfillmentTime}
          disabled={disableFields}
          // When an existing recurring order is being edited
          // We don't want to show an error if the start date is before today- it almost always will be
          minDate={!isRecurring ? undefined : utils.todayStart(timezone)}
          maxDate={isRecurring && !recursForever ? (endDate ?? undefined) : undefined}
          onError={(newError) => setStartDateError(newError)}
          textFieldProps={{
            helperText: startDateErrorMessage,
          }}
        />
        <Help
          tooltip={
            "If this is a recurring order, then the start date is when the first orders will start to be created. Once you add crops and blends, the order dates will appear below. If this is for a one-time order, then the order date is when you want to deliver the order. If your crops don't have a harvest buffer, then the harvest dates will be the same as the order date."
          }
        />
      </Box>
      {isRecurring && !recursForever && (
        <InternalDatePicker
          minDate={isRecurring ? (startDate ?? utils.todayStart(timezone)) : undefined}
          label="End Date *"
          value={endDate}
          onChange={onChangeEndDate}
          disabled={disableFields}
        />
      )}
      {isRecurring && (
        <>
          <Box>
            <FormControl fullWidth>
              <InputLabel id="frequency-unit-selector">Frequency Type</InputLabel>
              <Select
                aria-describedby="frequency-unit-selector"
                id="frequency-unit-selector"
                labelId="frequency-unit-selector"
                label="Frequency Type"
                value={frequencyUnit}
                onChange={onFrequencyUnitChange}
                // onEnter={onEnter}
                required={true}
                disabled={disableFields}
              >
                {orderFrequencyOptions.map((option) => {
                  return (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>
          <Box>
            {frequencyUnit === EVERY_X_DAY_OF_WEEK ? (
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                <Typography>Every</Typography>
                <FormControl fullWidth>
                  <InputLabel id="frequency-day-of-week-selector">Day of Week</InputLabel>
                  <Select
                    aria-describedby="frequency-day-of-week-selector"
                    id="frequency-day-of-week-selector"
                    labelId="frequency-day-of-week-selector"
                    label="Day of Week"
                    value={dayOfWeek}
                    onChange={onDayOfWeekChange}
                    // onEnter={onEnter}
                    required={true}
                    disabled={disableFields}
                  >
                    {daysOfTheWeek.map((day) => {
                      return (
                        <MenuItem key={day} value={day}>
                          {day}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Box>
            ) : frequencyUnit === EVERY_NTH_WEEKDAY_OF_MONTH ? (
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                <Typography>Every</Typography>
                <FormControl fullWidth>
                  <InputLabel id="frequency-nth-week-of-month-selector">Occurrence</InputLabel>
                  <Select
                    aria-describedby="frequency-nth-week-of-month-selector"
                    id="frequency-nth-week-of-month-selector"
                    labelId="frequency-nth-week-of-month-selector"
                    label="Occurence"
                    value={occurence}
                    onChange={onChangeOccurence}
                    // onEnter={onEnter}
                    required={true}
                    disabled={disableFields}
                  >
                    {[
                      { value: '0', label: '1st' },
                      { value: '1', label: '2nd' },
                      { value: '2', label: '3rd' },
                      { value: '3', label: '4th' },
                    ].map((occ) => {
                      return (
                        <MenuItem key={occ.value} value={occ.value}>
                          {occ.label}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel id="frequency-day-of-week-selector">Day of Week</InputLabel>
                  <Select
                    aria-describedby="frequency-day-of-week-selector"
                    id="frequency-day-of-week-selector"
                    labelId="frequency-day-of-week-selector"
                    label="Day of Week"
                    value={dayOfWeek}
                    onChange={onDayOfWeekChange}
                    // onEnter={onEnter}
                    required={true}
                    disabled={disableFields}
                  >
                    {daysOfTheWeek.map((day) => {
                      return (
                        <MenuItem key={day} value={day}>
                          {day}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <Typography>of the month</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                <Typography>Every</Typography>
                <FormControl fullWidth>
                  <InputLabel id="weekly-recurring-order-frequency">Frequency</InputLabel>
                  <Select
                    aria-describedby="weekly-recurring-order-frequency"
                    id="weekly-recurring-order-frequency"
                    labelId="weekly-recurring-order-frequency"
                    label="Frequency"
                    value={frequency}
                    onChange={onChangeFrequencySelect}
                    // onEnter={onEnter}
                    required={true}
                    disabled={disableFields}
                  >
                    {Array.from({ length: MAX_ORDER_WEEKLY_FREQUENCY }, (_, i) => (i + 1).toString()).map((freq) => {
                      return (
                        <MenuItem key={freq} value={freq}>
                          {freq}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <Typography>weeks</Typography>
              </Box>
            )}
          </Box>
        </>
      )}
      <TextField
        aria-describedby="order-notes"
        id="order-notes"
        label="Notes"
        value={notes}
        onChange={onChangeNotes}
        onEnter={onEnter}
        disabled={disableFields}
      />
      {
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          {cropOrBlendForms.map((cropOrBlendForm) => (
            <Box key={cropOrBlendForm.props.formKey}>{cropOrBlendForm}</Box>
          ))}
        </Box>
      }
      <Box>
        <Grid container display="flex" flexDirection="row" alignItems="center">
          <Button
            variant="outlined"
            arial-label="add-crop-or-blend"
            onClick={addCropOrBlendToUseForm}
            disabled={createOrderLoading || createRecurringOrderLoading}
            startIcon={<AddCircleOutlineIcon />}
          >
            Add Crop/Blend
          </Button>
          <Grid flexGrow="1" />
        </Grid>
      </Box>
      {isRecurring && (
        <Box display="flex" flexDirection="row" flexWrap="nowrap" alignItems="center" justifyContent="flex-start">
          <FormControlLabel
            label="Create Overdue Orders"
            control={<Checkbox checked={shouldCreateOverdueOrders} onChange={onChangeShouldCreateOverdueOrders} />}
          />
          <Help tooltip="By checking this, Microgreen Manager will create orders that are too late to plant, given the delivery date and the grow time of the crops needed. This is useful for when you are first getting setup with Microgreen Manager. See the Tutorials page for a more detailed explanation." />
        </Box>
      )}
      {!isRecurring && (
        <Box>
          <Box display="flex" flexDirection="row" flexWrap="nowrap" alignItems="center" justifyContent="flex-start">
            <FormControlLabel
              label="Use Surpluses"
              control={<Checkbox checked={useSurpluses} onChange={onChangeUseSurpluses} />}
            />
            <Help tooltip="By checking this, Microgreen Manager will use existing farm surpluses to fulfill part or all of the order." />
          </Box>
          <Box display="flex" flexDirection="row" flexWrap="nowrap" alignItems="center" justifyContent="flex-start">
            <FormControlLabel
              label="Create Overdue Plantings"
              control={<Checkbox checked={createOverduePlantings} onChange={onChangeCreateOverduePlantings} />}
            />
            <Help tooltip="By checking this, Microgreen Manager will create plantings for the order that can't be started in time." />
          </Box>
        </Box>
      )}
      {longestTotalGrowTimePlusBuffer !== Number.NEGATIVE_INFINITY && isRecurring && (
        <>
          <Collapse in={shouldCreateOverdueOrders} timeout="auto" unmountOnExit>
            <Typography>Overdue orders will be created for these dates:</Typography>
            {overdueTimes.length > 0 ? (
              <ul>
                {overdueTimes.map(({ time }) => {
                  return <li key={time}>{formatTime(time, { timezone })}</li>;
                })}
              </ul>
            ) : (
              <ul>
                <li>No overdue orders will be created.</li>
              </ul>
            )}
          </Collapse>
          <Typography>New orders will be created for these dates:</Typography>
          {futureTimes.length > 0 ? (
            <ul>
              {futureTimes.map(({ time, formattedTime }) => {
                return <li key={time}>{formattedTime}</li>;
              })}
            </ul>
          ) : (
            <ul>
              <li>No new upcoming orders will be created now.</li>
            </ul>
          )}
        </>
      )}
      {!isRecurring && allChanges.length > 0 && (
        <AddOrderChangesTable
          plantings={allChanges}
          useSurpluses={useSurpluses}
          createOverduePlantings={createOverduePlantings}
        />
      )}
    </Box>
  );

  let dialogContent = form;
  if (isForRecurringOrderClone) {
    dialogContent = recurringOrderDataError ? (
      <ErrorView error={recurringOrderDataError} />
    ) : recurringOrderDataLoading || recurringOrderData?.recurring_order == null ? (
      <PageLoading />
    ) : (
      form
    );
  } else if (isForSingleOrderClone) {
    dialogContent = orderDataError ? (
      <ErrorView error={orderDataError} />
    ) : orderDataLoading || orderData?.order == null ? (
      <PageLoading />
    ) : (
      form
    );
  }

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        onClose();
      }}
      maxWidth="md"
    >
      <DialogTitle>
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{`New ${isRecurring ? 'Recurring' : 'Single'} Order`}</Typography>
          <ExitDialogButton dontShiftUp onCloseDialog={onClose} />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ padding: 1 }}>{dialogContent}</DialogContent>
      <DialogActions disableSpacing sx={{ flexDirection: { md: 'row', xs: 'column' }, alignItems: 'center' }}>
        <Button sx={{ width: { xs: '100%', md: '50%' } }} variant="outlined" onClick={onClose} disabled={disableFields}>
          Cancel
        </Button>
        <Button
          id="submit-order-button"
          sx={{ width: { xs: '100%', md: '50%' }, ml: { md: 1 }, mt: { xs: 1, md: 0 } }}
          variant="contained"
          onClick={onSubmit}
          type="submit"
          disabled={isSubmitDisabled}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
